import { useCallback, useEffect, useState } from 'react';
import { useChannelCuration, useChannelRead } from '@lemontaigu/staff/react';
import { useCapability } from '@lemontaigu/platform-ui';
import { CAPABILITIES } from '@lemontaigu/platform';
import type { KnowledgeCandidate, StaffDigest } from '@lemontaigu/staff';
import { fmtDayLabel } from './logic.js';

// « Synthèses » (S2): the nightly AI digests (team-facing) and, for the
// operator only, the pending knowledge candidates with edit-then-accept —
// the C9 "never auto-saved" review posture. Accepting creates a `fact` row
// in chat_notices via the staff_candidate_accept RPC, feeding the guest
// concierge's dynamic KB directly.

export function Syntheses() {
  const channel = useChannelRead();
  const [digests, setDigests] = useState<StaffDigest[] | null>(null);
  const [candidates, setCandidates] = useState<KnowledgeCandidate[]>([]);
  const [err, setErr] = useState('');
  // Ladder step 8: was a `supabase.rpc('is_admin')` inside `load()`. Now read
  // from the set fetched once per session (ADR-0013).
  //
  // It still guards the pendingCandidates() FETCH, not just the rendering —
  // that read is is_admin-gated in the database and would error for the shared
  // desk login. Capabilities decide what to ASK for as well as what to show;
  // the policy is still what refuses it.
  const isOperator = useCapability(CAPABILITIES.PLATFORM_ADMIN);

  const load = useCallback(async () => {
    try {
      setDigests(await channel.recentDigests());
      if (isOperator) {
        setCandidates(await channel.pendingCandidates());
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [channel, isOperator]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      {err && <p className="auth-err">Erreur : {err}</p>}

      {isOperator && candidates.length > 0 && (
        <section className="cand-section">
          <h2 className="section-title">À valider — connaissances proposées</h2>
          <p className="auth-hint">
            Extraites automatiquement des échanges. Relisez, corrigez si besoin, puis validez : le
            fait entre dans la base de connaissances (assistant clients inclus).
          </p>
          {candidates.map((c) => (
            <CandidateCard key={c.id} candidate={c} onDecided={load} />
          ))}
        </section>
      )}

      <h2 className="section-title">Synthèses quotidiennes</h2>
      {digests === null && <p className="staff-empty">Chargement…</p>}
      {digests !== null && digests.length === 0 && (
        <p className="staff-empty">
          Aucune synthèse pour l’instant — la première est générée automatiquement en fin de
          journée.
        </p>
      )}
      {digests?.map((d) => (
        <article key={d.digestDate} className="digest-card">
          <div className="digest-head">
            <span className="digest-date">{fmtDayLabel(d.digestDate)}</span>
            <span className="digest-count">
              {d.messageCount} message{d.messageCount === 1 ? '' : 's'}
            </span>
          </div>
          <div className="digest-body">{d.bodyFr}</div>
        </article>
      ))}
    </div>
  );
}

function CandidateCard({
  candidate,
  onDecided,
}: {
  candidate: KnowledgeCandidate;
  onDecided: () => void;
}) {
  const channelCuration = useChannelCuration();
  const [body, setBody] = useState(candidate.bodyFr);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const decide = async (action: 'accept' | 'dismiss') => {
    setBusy(true);
    setErr('');
    try {
      if (action === 'accept') await channelCuration.accept(candidate.id, body.trim());
      else await channelCuration.dismiss(candidate.id);
      onDecided();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cand-card">
      <div className="cand-date">{fmtDayLabel(candidate.digestDate)}</div>
      <textarea
        className="cand-edit"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
      />
      {err && <p className="auth-err">{err}</p>}
      <div className="cand-actions">
        <button
          type="button"
          className="auth-btn"
          disabled={busy || body.trim() === ''}
          onClick={() => void decide('accept')}
        >
          Valider comme fiche
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={() => void decide('dismiss')}>
          Ignorer
        </button>
      </div>
    </div>
  );
}
